import React, { FC } from 'react';

// eslint-disable-next-line no-restricted-syntax
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { useFlag } from '@modernloop/shared/feature-flag';
import { CrossIcon } from '@modernloop/shared/icons';
import { ButtonBase, IconButton, Stack, Theme, Typography } from '@mui/material';

import { AtsType, Permission } from 'src/generated/mloop-graphql';

import DoNotShowAgainKey from 'src/components/Dialog/ConfirmationDialog/DoNotShowAgainKey';
import useDoNotShowAgain from 'src/components/Dialog/ConfirmationDialog/useDoNotShowAgain';

import useEmployeeIntegrations from 'src/hooks/api/integration/employee/useEmployeeIntegrations';
import { useHasUserPermission } from 'src/hooks/api/user/get';
import { getAtsService, useOrgAtsService } from 'src/hooks/atsService';
import useHistory from 'src/hooks/useHistory';

const GREENHOUSE_OAUTH_STATUS_QUERY = gql`
  query GreenhouseHarvestV3BannerOAuthStatus {
    thisOrg {
      id
      integrations {
        id
        greenhouseOauth {
          subdomain
        }
      }
    }
  }
`;

function getTodayKey(base: string): DoNotShowAgainKey {
  const today = new Date().toISOString().slice(0, 10);
  return `${base}_${today}` as DoNotShowAgainKey;
}

interface BannerContentProps {
  text: string;
  actionLabel: string;
  onAction: () => void;
  onClose: () => void;
}

const BannerContent: FC<BannerContentProps> = ({ text, actionLabel, onAction, onClose }) => (
  <Stack
    direction="row"
    sx={{
      padding: '8px 20px',
      backgroundColor: (theme: Theme) => theme.palette.warning.main,
      borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    }}
    justifyContent="space-between"
    spacing={1}
  >
    <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
      <Typography sx={{ color: (theme: Theme) => theme.palette.warning.contrastText }}>{text}</Typography>
      <ButtonBase
        onClick={onAction}
        sx={{
          color: (theme: Theme) => theme.palette.warning.contrastText,
          marginLeft: '8px',
          textDecoration: 'underline',
        }}
      >
        <Typography>{actionLabel}</Typography>
      </ButtonBase>
    </Stack>
    <IconButton onClick={onClose} size="large">
      <CrossIcon />
    </IconButton>
  </Stack>
);

const GreenhouseHarvestV3Banner: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const showGreenhouseV3UiPrompts = useFlag('show_greenhouse_v3_ui_prompts');
  const canManageOrgSettings = useHasUserPermission(Permission.CanManageOrgSettings);
  const canSchedule = useHasUserPermission(Permission.CanSchedule);
  const currentAtsService = useOrgAtsService();
  const isGreenhouseOrg = currentAtsService === getAtsService(AtsType.Greenhouse);

  const orgBannerKey = getTodayKey('greenhouse_harvest_v3_banner_org');
  const employeeBannerKey = getTodayKey('greenhouse_harvest_v3_banner_employee');
  const employeeReauthBannerKey = getTodayKey('greenhouse_harvest_v3_banner_employee_reauth');

  const {
    isDoNotShowAgainLoading: orgDismissLoading,
    doNotShowAgain: orgDismissed,
    addToDoNotShowAgain: dismissOrgBanner,
  } = useDoNotShowAgain(orgBannerKey);

  const {
    isDoNotShowAgainLoading: employeeDismissLoading,
    doNotShowAgain: employeeDismissed,
    addToDoNotShowAgain: dismissEmployeeBanner,
  } = useDoNotShowAgain(employeeBannerKey);

  const {
    isDoNotShowAgainLoading: employeeReauthDismissLoading,
    doNotShowAgain: employeeReauthDismissed,
    addToDoNotShowAgain: dismissEmployeeReauthBanner,
  } = useDoNotShowAgain(employeeReauthBannerKey);

  const {
    data: orgOAuthData,
    loading: orgOAuthLoading,
    error: orgOAuthError,
  } = useQuery(GREENHOUSE_OAUTH_STATUS_QUERY, {
    skip: !showGreenhouseV3UiPrompts || !isGreenhouseOrg,
  });
  const hasOrgOAuth = Boolean(orgOAuthData?.thisOrg?.integrations?.greenhouseOauth);

  const { data: employeeIntegrations, isLoading: employeeLoading } = useEmployeeIntegrations();
  const hasEmployeeOAuth = Boolean(employeeIntegrations?.greenhouse_oauth_employee?.active);
  const needsEmployeeReauth = Boolean(employeeIntegrations?.greenhouse_oauth_employee?.needs_reauth);

  if (
    !showGreenhouseV3UiPrompts ||
    !isGreenhouseOrg ||
    !canSchedule ||
    orgOAuthLoading ||
    orgOAuthError ||
    employeeLoading ||
    orgDismissLoading ||
    employeeDismissLoading ||
    employeeReauthDismissLoading
  ) {
    return null;
  }

  if (!hasOrgOAuth && canManageOrgSettings && !orgDismissed) {
    return (
      <BannerContent
        text={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.orgSetup')}
        actionLabel={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.goToIntegrations')}
        onAction={() => history.push('/settings/organization/integrations')}
        onClose={() => dismissOrgBanner().catch(() => {})}
      />
    );
  }

  if (hasEmployeeOAuth && needsEmployeeReauth && !employeeReauthDismissed) {
    return (
      <BannerContent
        text={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.employeeReauth')}
        actionLabel={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.reconnectAccount')}
        onAction={() => history.push('/settings/account/apps')}
        onClose={() => dismissEmployeeReauthBanner().catch(() => {})}
      />
    );
  }

  if (!hasEmployeeOAuth && !employeeDismissed) {
    return (
      <BannerContent
        text={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.employeeSetup')}
        actionLabel={t('hiring-dashboard-common:common.greenhouseHarvestV3Banner.connectAccount')}
        onAction={() => history.push('/settings/account/apps')}
        onClose={() => dismissEmployeeBanner().catch(() => {})}
      />
    );
  }

  return null;
};

export default GreenhouseHarvestV3Banner;
